import { selectProfileData } from '../../../../store/workspace/selectors';
import { getProfileDataThunk } from '../../../../store/workspace/thunks';
import { useScreenRecorder } from '../../../../hooks/useScreenRecorder';
import { RecordedChunk } from '../../../../utils/media/ChunkedRecorder';
import { useVideoCapture } from '../../../../hooks/useVideoCapture';
import { uploadFileThunk } from '../../../../store/general/thunks';
import { CodingExamQuestion } from '../../../../store/exam/types';
import { useDispatch, useSelector } from '../../../../store/store';
import { getUserInfoThunk } from '../../../../store/auth/thunks';
import { setExitExamModal } from '../../../../store/exam/slice';
import { selectUserId } from '../../../../store/auth/selectors';
import { useCountDown } from '../../../../hooks/useCountDown';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
    postCodingExamAnswerThunk,
    getCodingExamQuestionThunk,
} from '../../../../store/exam/thunks';
import {
    QUESTION_COUNT,
    ALREADY_ANSWERED,
    CAMERA_CHECK_INTERVAL_MS,
} from '../constants';
import { initialExamLanguage, toExamLanguages } from '../../constants';
import {
    ExamPhase,
    ExamResponse,
    AnswerRequest,
    ExamRouteState,
    QuestionRequest,
} from '../types';

/**
 * Drives a coding exam: fetches one question at a time, keeps the countdown and both
 * recordings — webcam and shared screen — in step with it, and reports every answer.
 *
 * The webcam pauses over the break between questions while the screen recording rolls
 * straight into the next question's file, so nothing that happens on screen is missed.
 */
export const useCodingExam = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();
    const userId = useSelector(selectUserId);
    const profile = useSelector(selectProfileData);
    const { counter, pause, restart } = useCountDown();
    const { jobId, examId, jobTitle, langs, action, translateLanguages } = (state || {}) as ExamRouteState;
    const examLanguages = toExamLanguages(translateLanguages);

    const [answer, setAnswer] = useState('');
    const [selectedLang, setSelectedLang] = useState('');
    const [examLanguage, setExamLanguage] = useState(() => initialExamLanguage(examLanguages));
    const [isLoading, setIsLoading] = useState(false);
    const [questionNumber, setQuestionNumber] = useState(1);
    const [cameraAccess, setCameraAccess] = useState(true);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [phase, setPhase] = useState<ExamPhase>('KYC');
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [question, setQuestion] = useState<CodingExamQuestion | null>(null);

    /** A single accepted language leaves nothing to choose — the exam is in that one. */
    const language = (langs && langs.length > 1 ? selectedLang : langs?.[0]) || '';

    // The chunk callbacks run outside React's render cycle, so the destination they need
    // is mirrored here rather than read from a stale closure.
    const uploadPathRef = useRef<string | null>(null);

    const uploadChunk = async (
        { file, fileName }: RecordedChunk,
        callback?: (percentage: number) => void,
    ) => {
        const path = uploadPathRef.current;
        if (!path) return;

        await dispatch(uploadFileThunk({ file, callback, fileName: `${path}/${fileName}` }));
    };

    const {
        stopRecording,
        startRecording,
        nextChunk: nextScreenChunk,
        recording: isScreenRecording,
        unavailable: screenRecordFailed,
        setChunkIndex: setScreenChunkIndex,
    } = useScreenRecorder({
        // The screen recording is the larger of the two, so it drives the progress bar.
        onChunk: chunk => uploadChunk(chunk, setUploadProgress),
    });

    const {
        webcamRef,
        stopCapture,
        startCapture,
        setChunkIndex,
    } = useVideoCapture({
        onChunk: uploadChunk,
    });

    const fetchQuestion = (body: QuestionRequest): Promise<ExamResponse<CodingExamQuestion>> =>
        dispatch(getCodingExamQuestionThunk(body)).unwrap();

    const submitAnswer = (body: AnswerRequest): Promise<unknown> =>
        dispatch(postCodingExamAnswerThunk(body)).unwrap();

    useEffect(() => {
        if (!jobId) navigate('/workspace');
        if (!userId) dispatch(getUserInfoThunk());
        if (!profile) dispatch(getProfileDataThunk());
    }, []);

    useEffect(() => {
        uploadPathRef.current = userId ? `${userId}/${jobId}/${examId}` : null;
    }, [userId, jobId, examId]);

    useEffect(() => {
        // The screen has to be shared before the applicant is allowed to start.
        if (phase === 'PreExam') startRecording();
    }, [phase]);

    useEffect(() => {
        // Out of time — hand in whatever has been written.
        if (counter === 0) submit();
    }, [counter]);

    useEffect(() => {
        /** A permission granted at the start can still be revoked mid-exam. */
        const checkCamera = () => {
            const videoTrack = webcamRef.current?.stream?.getVideoTracks()[0];
            if (!videoTrack || (videoTrack.enabled && videoTrack.readyState !== 'ended')) return;

            setIsCameraActive(false);
            setCameraAccess(false);
        };

        const interval = setInterval(checkCamera, CAMERA_CHECK_INTERVAL_MS);
        navigator.mediaDevices.addEventListener('devicechange', checkCamera);

        return () => {
            clearInterval(interval);
            navigator.mediaDevices.removeEventListener('devicechange', checkCamera);
        };
    }, []);

    /** Reports the last answer and holds the applicant until both recordings are up. */
    const finishExam = async (body: AnswerRequest) => {
        await submitAnswer(body);

        setPhase('CollectingData');
        // Stay on the "collecting your data" screen until the last chunk is uploaded.
        await Promise.all([stopCapture(), stopRecording()]);
        setPhase('ExamEnd');
    };

    const startExam = async () => {
        if (!jobId || !language) return;
        setIsLoading(true);

        try {
            const { statusCode, body } = await fetchQuestion({ jobId, language, lang: examLanguage });

            if (statusCode === ALREADY_ANSWERED) {
                await submitAnswer({ jobId, answer });
                setPhase('ExamEnd');
                return;
            }

            setQuestion(body);
            setQuestionNumber(body.questionNumber);
            // A resumed exam picks up mid-way, so number the recordings after the
            // question they cover instead of restarting from one.
            setChunkIndex(body.questionNumber);
            setScreenChunkIndex(body.questionNumber);
            setPhase('Question');
            restart(body.solving_time * 60);
        } catch (error) {
            console.warn(error);
        } finally {
            setIsLoading(false);
        }
    };

    /** Hands the current answer in, then breaks for the next question or ends the exam. */
    const submit = async () => {
        if (!jobId || !question || phase !== 'Question') return;

        setIsLoading(true);
        pause();
        // The question scrolls away underneath it, so drop any leftover text selection.
        window.getSelection?.()?.removeAllRanges();

        try {
            const body = { jobId, answer, language: question.language };

            if (questionNumber === QUESTION_COUNT) {
                await finishExam(body);
                return;
            }

            await submitAnswer(body);
            setPhase('Break');
            stopCapture();
            nextScreenChunk();
        } catch (error) {
            console.warn(error);
        } finally {
            setIsLoading(false);
        }
    };

    const nextQuestion = async () => {
        if (!jobId) return;
        setIsLoading(true);

        try {
            const { body } = await fetchQuestion({ jobId, lang: examLanguage });

            setAnswer('');
            setQuestion(body);
            setQuestionNumber(prev => prev + 1);
            setPhase('Question');
            startCapture();
            // No-op unless the applicant ended the share during the break.
            startRecording();
            restart(body.solving_time * 60);
        } catch (error) {
            console.warn(error);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        phase,
        action,
        jobId,
        examId,
        userId,
        langs,
        counter,
        profile,
        question,
        jobTitle,
        webcamRef,
        language,
        isLoading,
        selectedLang,
        examLanguage,
        examLanguages,
        cameraAccess,
        isCameraActive,
        uploadProgress,
        questionNumber,
        isScreenRecording,
        screenRecordFailed,
        submit,
        startExam,
        setAnswer,
        nextQuestion,
        startCapture,
        setSelectedLang,
        setExamLanguage,
        startScreenRecord: startRecording,
        enableCamera: () => setCameraAccess(true),
        onCameraStream: (stream: MediaStream) => setIsCameraActive(stream.active),
        onCameraError: () => {
            setIsCameraActive(false);
            setCameraAccess(false);
        },
        exitExam: () => dispatch(setExitExamModal(true)),
        onKYCSuccess: () => setPhase('PreExam'),
    };
};
